import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import Papa from "papaparse";
import { api } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { UploadSimple, Plus, TestTube, Trash, CheckCircle, PencilSimple, Broadcast, Eye, EyeSlash, DownloadSimple, Info, X, ClipboardText } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Senders() {
  const { provider } = useParams(); // "google" | "microsoft"
  const [senders, setSenders] = useState([]);
  const [stats, setStats] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterProxy, setFilterProxy] = useState("");
  const [showPaste, setShowPaste] = useState(false);
  const [paste, setPaste] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testProgress, setTestProgress] = useState(null); // { total, done, valid, failed, cancelled }
  const [uploadProgress, setUploadProgress] = useState(null); // { total, done, inserted, dup, invalid, cancelled }
  const testCancelRef = useRef(false);
  const uploadCancelRef = useRef(false);
  const [validation, setValidation] = useState(null); // { file, report, rows }
  const uploadRef = useRef(null);
  const [page, setPage] = useState(0);      // 0-based page index
  const [pageSize, setPageSize] = useState(500);
  const [totalCount, setTotalCount] = useState(0);

  const providerLabel = provider === "google" ? "Google Workspace" : "Microsoft 365";

  const load = async () => {
    const params = { provider, limit: pageSize, skip: page * pageSize };
    if (search) params.search = search;
    if (filterStatus) params.status = filterStatus;
    if (filterProxy) params.proxy_status = filterProxy;
    const r = await api.get("/senders", { params });
    setSenders(r.data.senders);
    setTotalCount(r.data.total || 0);
    const s = await api.get("/senders/stats");
    setStats(s.data);
  };

  useEffect(() => {
    setSelected(new Set());
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, filterStatus, filterProxy]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, filterStatus, filterProxy, page, pageSize]);

  const SENDERS_BATCH_SIZE = 50;

  // Parse CSV in the browser using PapaParse. Returns { rows, headers, error }.
  const parseCsvFile = (file) =>
    new Promise((resolve) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: "greedy",
        transformHeader: (h) => (h || "").trim(),
        complete: (res) => resolve({
          rows: (res.data || []).map((r, i) => ({ ...r, _row: i + 2 })), // +2: header is row 1
          headers: res.meta?.fields || [],
          error: null,
        }),
        error: (err) => resolve({ rows: [], headers: [], error: err.message }),
      });
    });

  const upload = async (f) => {
    // Two-phase flow: first validate (no writes), then let the user confirm.
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post(`/senders/validate-csv?provider=${provider}`, fd);
      // Also parse the file client-side so we can stream inserts in batches
      // after the user confirms.
      const parsed = await parseCsvFile(f);
      setValidation({ file: f, report: r.data, rows: parsed.rows });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Validation failed");
    }
  };

  const confirmUpload = async () => {
    if (!validation || !validation.rows) return;
    const rows = validation.rows;
    setValidation(null);
    uploadCancelRef.current = false;
    setUploadProgress({ total: rows.length, done: 0, inserted: 0, dup: 0, invalid: 0, cancelled: false });
    try {
      for (let start = 0; start < rows.length; start += SENDERS_BATCH_SIZE) {
        if (uploadCancelRef.current) {
          setUploadProgress((p) => ({ ...p, cancelled: true }));
          break;
        }
        const chunk = rows.slice(start, start + SENDERS_BATCH_SIZE);
        try {
          const r = await api.post("/senders/upload-batch", { provider, rows: chunk });
          const d = r.data || {};
          setUploadProgress((p) => ({
            ...p,
            done: p.done + chunk.length,
            inserted: p.inserted + (d.inserted || 0),
            dup: p.dup + (d.skipped_duplicate || 0),
            invalid: p.invalid + (d.skipped_invalid || 0),
          }));
        } catch (e) {
          setUploadProgress((p) => ({
            ...p,
            done: p.done + chunk.length,
            invalid: p.invalid + chunk.length,
          }));
        }
        // Reload table after every batch so counts + rows appear live.
        load();
      }
      setUploadProgress((p) => {
        const label = uploadCancelRef.current ? "Upload cancelled" : "Upload complete";
        toast.success(`${label} · ${p.inserted} imported · ${p.dup} duplicates · ${p.invalid} invalid`);
        return p;
      });
    } finally {
      setTimeout(() => setUploadProgress(null), 4000);
    }
  };

  const cancelUpload = () => { uploadCancelRef.current = true; };

  const downloadSample = async () => {
    try {
      const r = await api.get(`/senders/sample-csv`, {
        params: { provider },
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `senders-${provider}-sample.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Sample CSV downloaded`);
    } catch (e) {
      toast.error("Failed to download sample");
    }
  };

  const exportWithCredentials = async () => {
    try {
      const r = await api.get(`/senders/export-full`, {
        params: { provider },
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `senders-${provider}-with-credentials.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Exported with original passwords");
    } catch (e) {
      toast.error("Failed to export");
    }
  };

  const toggleAll = () => {
    if (selected.size === senders.length) setSelected(new Set());
    else setSelected(new Set(senders.map((s) => s.id)));
  };

  const bulk = async (action) => {
    if (selected.size === 0) return;
    await api.post("/senders/bulk-action", { ids: Array.from(selected), action });
    toast.success(`Bulk ${action} · ${selected.size} senders`);
    setSelected(new Set());
    load();
  };

  const applyPaste = async () => {
    const emails = paste
      .split(/[\s,;]+/)
      .map((e) => e.trim().toLowerCase())
      .filter((e) => e.includes("@"));
    if (!emails.length) { toast.error("Paste some email addresses first"); return; }
    try {
      const { data } = await api.post("/senders/lookup", { emails, provider });
      const matched = data.matched || [];
      const missed = data.missed || [];
      if (!matched.length) { toast.error(`No matching ${providerLabel} senders found`); return; }
      setSelected((prev) => {
        const n = new Set(prev);
        matched.forEach((m) => n.add(m.id));
        return n;
      });
      toast.success(`Selected ${matched.length} sender${matched.length === 1 ? "" : "s"}${missed.length ? ` · ${missed.length} not found` : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lookup failed");
    }
  };

  // Batched proxy test: 50 IDs per request. Progress state drives an inline
  // banner + progress bar. The backend runs 10 in parallel per batch, so this
  // keeps memory + timeouts bounded while streaming visible progress.
  const PROXY_TEST_BATCH_SIZE = 50;

  const testSelected = async () => {
    // If the user has explicit checkboxes selected, use those. Otherwise fetch
    // EVERY matching sender ID from the backend (respecting current filters)
    // so 'Test all' actually tests all, not just the visible page.
    let ids;
    if (selected.size > 0) {
      ids = Array.from(selected);
    } else {
      const params = { provider };
      if (search) params.search = search;
      if (filterStatus) params.status = filterStatus;
      if (filterProxy) params.proxy_status = filterProxy;
      try {
        const r = await api.get("/senders/ids", { params });
        ids = r.data.ids || [];
      } catch {
        toast.error("Could not fetch full sender list — showing only current page");
        ids = senders.map((s) => s.id);
      }
    }
    if (ids.length === 0) return;
    testCancelRef.current = false;
    setTesting(true);
    setTestProgress({ total: ids.length, done: 0, valid: 0, failed: 0, cancelled: false });
    try {
      for (let start = 0; start < ids.length; start += PROXY_TEST_BATCH_SIZE) {
        if (testCancelRef.current) {
          setTestProgress((p) => ({ ...p, cancelled: true }));
          break;
        }
        const chunk = ids.slice(start, start + PROXY_TEST_BATCH_SIZE);
        try {
          const r = await api.post("/senders/test-proxies", { ids: chunk });
          const results = r.data.results || [];
          const ok = results.filter((x) => x.success).length;
          const bad = results.length - ok;
          setTestProgress((p) => ({
            ...p,
            done: p.done + results.length,
            valid: p.valid + ok,
            failed: p.failed + bad,
          }));
        } catch (e) {
          // Count the failed batch as failed but keep going.
          setTestProgress((p) => ({
            ...p,
            done: p.done + chunk.length,
            failed: p.failed + chunk.length,
          }));
        }
        // Reload table so intermediate results become visible.
        load();
      }
      const final = testCancelRef.current ? "Cancelled" : "Proxy test complete";
      setTestProgress((p) => {
        toast.success(`${final} · ${p.valid}/${p.total} valid`);
        return p;
      });
    } finally {
      setTesting(false);
      // Keep progress banner visible for a short beat so the user sees the final count.
      setTimeout(() => setTestProgress(null), 4000);
    }
  };

  const cancelProxyTest = () => { testCancelRef.current = true; };

  return (
    <div className="p-8">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="micro mb-1">Senders / {providerLabel}</div>
          <h1 className="font-heading text-4xl font-bold tracking-tighter">{providerLabel}</h1>
        </div>
        <div className="flex gap-2">
          <input
            ref={uploadRef}
            type="file"
            accept=".csv"
            style={{ display: "none" }}
            data-testid="senders-csv-input"
            onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
          />
          <BtnSecondary onClick={downloadSample} testid="senders-sample-btn">
            <DownloadSimple size={14} weight="bold" /> Sample CSV
          </BtnSecondary>
          <BtnSecondary onClick={exportWithCredentials} testid="senders-export-btn">
            <DownloadSimple size={14} weight="bold" /> Export CSV
          </BtnSecondary>
          <BtnSecondary onClick={() => uploadRef.current?.click()} testid="senders-upload-btn">
            <UploadSimple size={14} weight="bold" /> Upload CSV
          </BtnSecondary>
          <BtnPrimary onClick={() => setShowAdd(true)} testid="senders-add-btn">
            <Plus size={14} weight="bold" /> Add sender
          </BtnPrimary>
        </div>
      </div>

      <CsvFormatHint provider={provider} providerLabel={providerLabel} />

      {uploadProgress && (
        <ProgressBanner
          testid="upload-progress"
          title="Uploading senders"
          progress={uploadProgress}
          onCancel={cancelUpload}
          stats={[
            { label: "Imported", value: uploadProgress.inserted, tone: "success" },
            { label: "Duplicates", value: uploadProgress.dup, tone: "warn" },
            { label: "Invalid", value: uploadProgress.invalid, tone: "error" },
          ]}
        />
      )}
      {testProgress && (
        <ProgressBanner
          testid="test-progress"
          title="Testing proxies"
          progress={testProgress}
          onCancel={cancelProxyTest}
          stats={[
            { label: "Valid", value: testProgress.valid, tone: "success" },
            { label: "Failed", value: testProgress.failed, tone: "error" },
          ]}
        />
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 border border-[#2A2A2A] mb-6">
        <MiniStat label="Total" v={stats.total} />
        <MiniStat label={providerLabel} v={provider === "google" ? stats.google : stats.microsoft} />
        <MiniStat label="Valid proxy" v={stats.valid_proxy} tone="success" />
        <MiniStat label="Failed proxy" v={stats.failed_proxy} tone="error" />
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <input
          data-testid="senders-search"
          placeholder="search email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (page === 0 ? load() : setPage(0))}
          className="bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white w-64 focus:border-[#E1FF00] focus:outline-none"
        />
        <Filter value={filterStatus} onChange={setFilterStatus} testid="filter-status"
          options={[["", "any status"], ["active", "active"], ["paused", "paused"]]} />
        <Filter value={filterProxy} onChange={setFilterProxy} testid="filter-proxy"
          options={[["", "any proxy"], ["valid", "valid"], ["not_tested", "not tested"],
                    ["invalid_credentials", "invalid creds"], ["connection_failed", "connection fail"],
                    ["timeout", "timeout"], ["ip_mismatch", "ip mismatch"]]} />
        <BtnSecondary onClick={() => setShowPaste((v) => !v)} testid="bulk-paste-toggle">
          <ClipboardText size={14} weight="bold" /> Bulk select
        </BtnSecondary>
        <div className="flex-1" />
        {selected.size > 0 && (
          <>
            <span className="micro" data-testid="selected-count">{selected.size} selected</span>
            <BtnSecondary onClick={() => bulk("activate")} testid="bulk-activate">Activate</BtnSecondary>
            <BtnSecondary onClick={() => bulk("pause")} testid="bulk-pause">Pause</BtnSecondary>
            <BtnDestructive onClick={() => bulk("delete")} testid="bulk-delete">Delete</BtnDestructive>
          </>
        )}
        <BtnSecondary onClick={testSelected} testid="test-proxies-btn" disabled={testing}>
          <TestTube size={14} weight="bold" /> {testing ? "TESTING…" : `Test ${selected.size > 0 ? selected.size : "all"} ${selected.size === 1 ? "proxy" : "proxies"}`}
        </BtnSecondary>
      </div>

      {showPaste && (
        <div className="border border-[#2A2A2A] bg-[#0A0A0A] mb-4 p-4" data-testid="bulk-paste-panel">
          <div className="micro mb-2 text-[#71717A]">
            Paste email addresses — one per line, or separated by commas/spaces. Matching {providerLabel} senders get selected (even across pages).
          </div>
          <textarea
            data-testid="bulk-paste-input"
            value={paste}
            onChange={(e) => setPaste(e.target.value)}
            rows={5}
            placeholder={"alice@yourcompany.com\nbob@yourcompany.com"}
            className="w-full bg-black border border-[#2A2A2A] px-3 py-2 text-xs font-mono text-white focus:border-[#E1FF00] focus:outline-none resize-y"
          />
          <div className="flex items-center gap-2 mt-2">
            <BtnPrimary onClick={applyPaste} testid="bulk-paste-apply">
              <CheckCircle size={14} weight="bold" /> Match &amp; select
            </BtnPrimary>
            <BtnSecondary onClick={() => setPaste("")} testid="bulk-paste-clear">Clear text</BtnSecondary>
            {selected.size > 0 && (
              <BtnSecondary onClick={() => setSelected(new Set())} testid="bulk-paste-deselect">
                Deselect all ({selected.size})
              </BtnSecondary>
            )}
          </div>
        </div>
      )}

      <div className="border border-[#2A2A2A] bg-[#0A0A0A] overflow-x-auto">
        <table className="dtable w-full" data-testid="senders-table">
          <thead>
            <tr>
              <th style={{ width: 32 }}>
                <input type="checkbox" checked={selected.size === senders.length && senders.length > 0} onChange={toggleAll}
                       data-testid="select-all" />
              </th>
              <th className="num">#</th>
              <th>Email</th>
              <th>Name</th>
              <th>Status</th>
              <th>Proxy</th>
              <th>Proxy Status</th>
              <th>Public IP</th>
              <th className="num">RT</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {senders.length === 0 && (
              <tr><td colSpan={10} className="text-center text-[#71717A] py-8 font-mono">[ no senders — upload a CSV ]</td></tr>
            )}
            {senders.map((s) => (
              <tr key={s.id}>
                <td>
                  <input type="checkbox" checked={selected.has(s.id)}
                         onChange={(e) => {
                           const n = new Set(selected);
                           e.target.checked ? n.add(s.id) : n.delete(s.id);
                           setSelected(n);
                         }} />
                </td>
                <td className="num data-num text-[#71717A]">{s.upload_order}</td>
                <td className="font-mono text-xs">{s.email}</td>
                <td>{s.full_name || "—"}</td>
                <td><StatusBadge status={s.status} /></td>
                <td className="font-mono text-xs text-[#A1A1AA]">
                  {s.proxy_host ? `${s.proxy_protocol}://${s.proxy_host}:${s.proxy_port}` : "—"}
                </td>
                <td><StatusBadge status={s.proxy_status} /></td>
                <td className="font-mono text-xs">
                  {s.proxy_public_ip ? (
                    <span className="flex items-center gap-1.5">
                      <span>{s.proxy_public_ip}</span>
                      {s.proxy_ip_family && (
                        <span className={`inline-block px-1 py-[1px] text-[9px] font-mono uppercase tracking-widest border ${s.proxy_ip_family === "ipv6" ? "border-[#00E5FF]/40 text-[#00E5FF]" : "border-[#2A2A2A] text-[#71717A]"}`}>
                          {s.proxy_ip_family === "ipv6" ? "v6" : "v4"}
                        </span>
                      )}
                    </span>
                  ) : "—"}
                </td>
                <td className="num data-num text-[#71717A]">{s.proxy_response_time_ms || "—"}ms</td>
                <td>
                  <div className="flex gap-1">
                    <button
                      data-testid={`sender-edit-${s.id}`}
                      onClick={() => setEditing(s)}
                      title="Edit sender / proxy"
                      className="h-7 px-2 flex items-center gap-1 border border-[#2A2A2A] hover:border-[#E1FF00] hover:text-[#E1FF00] text-[#A1A1AA] transition-colors text-[10px] font-mono uppercase tracking-wider"
                    >
                      <PencilSimple size={12} weight="bold" /> Edit
                    </button>
                    <button
                      data-testid={`sender-test-${s.id}`}
                      title="Test proxy connection"
                      onClick={async () => {
                        const r = await api.post(`/senders/${s.id}/test-proxy`);
                        if (r.data.success) {
                          const fam = r.data.ip_family ? ` (${r.data.ip_family})` : "";
                          const hint = r.data.protocol_used && r.data.protocol_used !== s.proxy_protocol
                            ? ` · works as ${r.data.protocol_used.toUpperCase()} — edit sender to update protocol` : "";
                          toast.success(`Valid · ${r.data.public_ip}${fam}${hint}`);
                        } else {
                          toast.error(r.data.error_message || "Failed");
                        }
                        load();
                      }}
                      className="h-7 px-2 flex items-center gap-1 border border-[#2A2A2A] hover:border-[#00E5FF] hover:text-[#00E5FF] text-[#A1A1AA] transition-colors text-[10px] font-mono uppercase tracking-wider"
                    >
                      <Broadcast size={12} weight="bold" /> Test
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination controls */}
      <Pagination
        page={page}
        pageSize={pageSize}
        total={totalCount}
        onPageChange={setPage}
        onPageSizeChange={(s) => { setPageSize(s); setPage(0); }}
        testidPrefix="senders-pagination"
      />


      {showAdd && <AddSenderModal provider={provider} onClose={() => { setShowAdd(false); load(); }} />}
      {editing && <EditSenderModal sender={editing} onClose={() => { setEditing(null); load(); }} />}
      {validation && (
        <ValidationModal
          report={validation.report}
          fileName={validation.file?.name || ""}
          onConfirm={confirmUpload}
          onCancel={() => setValidation(null)}
        />
      )}
    </div>
  );
}

function EditSenderModal({ sender, onClose }) {
  const [form, setForm] = useState({
    first_name: sender.first_name || "",
    last_name: sender.last_name || "",
    status: sender.status || "active",
    app_password: "",
    tenant_id: sender.tenant_id || "",
    proxy_protocol: sender.proxy_protocol || "http",
    proxy_host: sender.proxy_host || "",
    proxy_port: sender.proxy_port || 0,
    proxy_username: sender.proxy_username || "",
    proxy_password: "",
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showAppPw, setShowAppPw] = useState(false);
  const [showProxyPw, setShowProxyPw] = useState(false);
  const [loadingCreds, setLoadingCreds] = useState(false);

  const revealApp = async () => {
    if (showAppPw) { setShowAppPw(false); return; }
    setLoadingCreds(true);
    try {
      const r = await api.get(`/senders/${sender.id}/credentials`);
      setForm((f) => ({ ...f, app_password: r.data.app_password || "" }));
      setShowAppPw(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not fetch credentials");
    } finally { setLoadingCreds(false); }
  };

  const revealProxy = async () => {
    if (showProxyPw) { setShowProxyPw(false); return; }
    setLoadingCreds(true);
    try {
      const r = await api.get(`/senders/${sender.id}/credentials`);
      setForm((f) => ({ ...f, proxy_password: r.data.proxy_password || "" }));
      setShowProxyPw(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not fetch credentials");
    } finally { setLoadingCreds(false); }
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.entries(form).forEach(([k, v]) => {
        if (v === "" || v === null || v === undefined) return;
        payload[k] = k === "proxy_port" ? (Number(v) || null) : v;
      });
      await api.patch(`/senders/${sender.id}`, payload);
      toast.success("Sender updated");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const testNow = async () => {
    setTesting(true);
    try {
      // save first so the tested config matches what's in DB
      await submit();
      const r = await api.post(`/senders/${sender.id}/test-proxy`);
      if (r.data.success) {
        const hint = r.data.protocol_used && r.data.protocol_used !== form.proxy_protocol
          ? ` · switched to ${r.data.protocol_used.toUpperCase()}` : "";
        toast.success(`Valid · ${r.data.public_ip}${hint}`);
      } else {
        toast.error(r.data.error_message || "Test failed");
      }
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl bg-[#0A0A0A] border border-[#2A2A2A] max-h-[90vh] overflow-y-auto" data-testid="edit-sender-modal">
        <div className="p-5 border-b border-[#2A2A2A]">
          <div className="micro mb-1">Edit sender</div>
          <h3 className="font-heading text-xl font-semibold font-mono">{sender.email}</h3>
        </div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <Input label="First name" val={form.first_name} on={(v) => setForm({ ...form, first_name: v })} tid="edit-fn" />
          <Input label="Last name" val={form.last_name} on={(v) => setForm({ ...form, last_name: v })} tid="edit-ln" />
          <div>
            <div className="micro mb-1.5">Status</div>
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
              data-testid="edit-status"
              className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white">
              <option value="active">Active</option>
              <option value="paused">Paused</option>
            </select>
          </div>
          <PasswordInput
            label={showAppPw ? "App password (visible)" : "App password (leave blank to keep)"}
            val={form.app_password}
            on={(v) => setForm({ ...form, app_password: v })}
            revealed={showAppPw}
            onToggle={revealApp}
            loading={loadingCreds}
            tid="edit-apppw"
          />
          {sender.provider === "microsoft" && (
            <Input label="Tenant ID" val={form.tenant_id} on={(v) => setForm({ ...form, tenant_id: v })} tid="edit-tenant" />
          )}
          <div className="col-span-2 h-px bg-[#2A2A2A] my-2" />
          <div>
            <div className="micro mb-1.5">Proxy protocol</div>
            <select className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white"
                    value={form.proxy_protocol} onChange={(e) => setForm({ ...form, proxy_protocol: e.target.value })}
                    data-testid="edit-proxy-protocol">
              <option value="http">HTTP (recommended for most proxies)</option>
              <option value="https">HTTPS (TLS-to-proxy; rare)</option>
              <option value="socks5">SOCKS5</option>
            </select>
          </div>
          <Input label="Proxy host (IPv4, IPv6 or hostname)" val={form.proxy_host}
            on={(v) => setForm({ ...form, proxy_host: v })} tid="edit-proxy-host" />
          <Input label="Proxy port" val={form.proxy_port} on={(v) => setForm({ ...form, proxy_port: v })} tid="edit-proxy-port" />
          <Input label="Proxy user" val={form.proxy_username} on={(v) => setForm({ ...form, proxy_username: v })} tid="edit-proxy-user" />
          <PasswordInput
            label={showProxyPw ? "Proxy password (visible)" : "Proxy password (leave blank to keep)"}
            val={form.proxy_password}
            on={(v) => setForm({ ...form, proxy_password: v })}
            revealed={showProxyPw}
            onToggle={revealProxy}
            loading={loadingCreds}
            tid="edit-proxy-pw"
          />
        </div>
        <div className="p-5 border-t border-[#2A2A2A] flex gap-2 justify-end">
          <BtnSecondary onClick={onClose} testid="edit-cancel">Cancel</BtnSecondary>
          <BtnSecondary onClick={testNow} testid="edit-save-and-test" disabled={saving || testing}>
            {testing ? "TESTING…" : "Save & test proxy"}
          </BtnSecondary>
          <BtnPrimary onClick={submit} testid="edit-submit" disabled={saving}>
            {saving ? "SAVING..." : "Save changes"}
          </BtnPrimary>
        </div>
      </div>
    </div>
  );
}

function AddSenderModal({ provider, onClose }) {
  const [form, setForm] = useState({
    email: "", first_name: "", last_name: "", app_password: "", tenant_id: "",
    proxy_protocol: "http", proxy_host: "", proxy_port: 0,
    proxy_username: "", proxy_password: "",
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/senders", { ...form, provider, proxy_port: Number(form.proxy_port) || null });
      toast.success("Sender added");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl bg-[#0A0A0A] border border-[#2A2A2A]" data-testid="add-sender-modal">
        <div className="p-5 border-b border-[#2A2A2A]">
          <h3 className="font-heading text-xl font-semibold">Add sender</h3>
        </div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <Input label="Email" val={form.email} on={(v) => setForm({ ...form, email: v })} tid="input-email" />
          <Input label="App password" val={form.app_password} on={(v) => setForm({ ...form, app_password: v })} type="password" tid="input-apppw" />
          <Input label="First name" val={form.first_name} on={(v) => setForm({ ...form, first_name: v })} tid="input-fn" />
          <Input label="Last name" val={form.last_name} on={(v) => setForm({ ...form, last_name: v })} tid="input-ln" />
          {provider === "microsoft" && (
            <Input label="Tenant ID (optional)" val={form.tenant_id} on={(v) => setForm({ ...form, tenant_id: v })} tid="input-tenant" />
          )}
          <div className="col-span-2 h-px bg-[#2A2A2A] my-2" />
          <div>
            <div className="micro mb-1.5">Proxy protocol</div>
            <select className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white"
                    value={form.proxy_protocol} onChange={(e) => setForm({ ...form, proxy_protocol: e.target.value })}
                    data-testid="input-proxy-protocol">
              <option value="http">HTTP (recommended for most proxies)</option>
              <option value="https">HTTPS (TLS-to-proxy; rare)</option>
              <option value="socks5">SOCKS5</option>
            </select>
          </div>
          <Input label="Proxy host" val={form.proxy_host} on={(v) => setForm({ ...form, proxy_host: v })} tid="input-proxy-host" />
          <Input label="Proxy port" val={form.proxy_port} on={(v) => setForm({ ...form, proxy_port: v })} tid="input-proxy-port" />
          <Input label="Proxy user" val={form.proxy_username} on={(v) => setForm({ ...form, proxy_username: v })} tid="input-proxy-user" />
          <Input label="Proxy password" val={form.proxy_password} on={(v) => setForm({ ...form, proxy_password: v })} type="password" tid="input-proxy-pw" />
        </div>
        <div className="p-5 border-t border-[#2A2A2A] flex gap-2 justify-end">
          <BtnSecondary onClick={onClose} testid="add-cancel">Cancel</BtnSecondary>
          <BtnPrimary onClick={submit} testid="add-submit" disabled={saving}>
            {saving ? "SAVING..." : "Save sender"}
          </BtnPrimary>
        </div>
      </div>
    </div>
  );
}

function Input({ label, val, on, type = "text", tid }) {
  return (
    <label className="block">
      <span className="micro block mb-1.5">{label}</span>
      <input
        data-testid={tid}
        type={type}
        value={val}
        onChange={(e) => on(e.target.value)}
        className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white focus:border-[#E1FF00] focus:outline-none"
      />
    </label>
  );
}

function PasswordInput({ label, val, on, revealed, onToggle, loading, tid }) {
  return (
    <label className="block">
      <span className="micro block mb-1.5">{label}</span>
      <div className="relative">
        <input
          data-testid={tid}
          type={revealed ? "text" : "password"}
          value={val}
          onChange={(e) => on(e.target.value)}
          className="w-full bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 pr-10 text-sm text-white font-mono focus:border-[#E1FF00] focus:outline-none"
        />
        <button
          type="button"
          onClick={onToggle}
          disabled={loading}
          data-testid={`${tid}-reveal`}
          title={revealed ? "Hide password" : "Reveal stored password"}
          className="absolute right-1 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-[#71717A] hover:text-[#E1FF00] transition-colors disabled:opacity-50"
        >
          {revealed ? <EyeSlash size={14} weight="bold" /> : <Eye size={14} weight="bold" />}
        </button>
      </div>
    </label>
  );
}

function Filter({ value, onChange, options, testid }) {
  return (
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="bg-[#0A0A0A] border border-[#2A2A2A] px-3 py-2 text-sm text-white"
    >
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}

function MiniStat({ label, v, tone }) {
  const c = tone === "success" ? "text-[#00FF66]" :
            tone === "error" ? "text-[#FF3B30]" :
            tone === "warning" ? "text-[#FF9500]" : "text-white";
  return (
    <div className="border-r border-[#2A2A2A] last:border-r-0 p-4">
      <div className="micro mb-1">{label}</div>
      <div className={`data-num text-xl font-bold ${c}`}>{v ?? "—"}</div>
    </div>
  );
}

export function BtnPrimary({ children, onClick, testid, disabled }) {
  return (
    <button data-testid={testid} onClick={onClick} disabled={disabled}
      className="inline-flex items-center gap-1.5 bg-[#E1FF00] text-black font-semibold text-xs uppercase tracking-wider px-3 py-2 hover:bg-[#CCEE00] transition-colors disabled:opacity-50">
      {children}
    </button>
  );
}
export function BtnSecondary({ children, onClick, testid, disabled }) {
  return (
    <button data-testid={testid} onClick={onClick} disabled={disabled}
      className="inline-flex items-center gap-1.5 bg-transparent text-white border border-[#2A2A2A] hover:border-[#8E8E93] text-xs uppercase tracking-wider px-3 py-2 transition-colors disabled:opacity-50">
      {children}
    </button>
  );
}
export function BtnDestructive({ children, onClick, testid, disabled = false }) {
  return (
    <button data-testid={testid} onClick={onClick} disabled={disabled}
      className="inline-flex items-center gap-1.5 bg-transparent text-[#FF3B30] border border-[#FF3B30]/30 hover:border-[#FF3B30] hover:bg-[#FF3B30]/10 text-xs uppercase tracking-wider px-3 py-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-[#FF3B30]/30 disabled:hover:bg-transparent">
      {children}
    </button>
  );
}

// ---- CSV format hint ----------------------------------------------------
// Renders the expected column list + a sample row so users don't need to
// download the sample CSV just to see the required schema. Collapsible so it
// doesn't crowd the table for users who already know the format.
function CsvFormatHint({ provider, providerLabel }) {
  const [open, setOpen] = useState(false);
  const google = ["email", "firstName", "lastName", "appPassword",
    "proxyProtocol", "proxyHost", "proxyPort", "proxyUsername", "proxyPassword"];
  const microsoft = ["email", "firstName", "lastName", "appPassword", "tenantId",
    "proxyProtocol", "proxyHost", "proxyPort", "proxyUsername", "proxyPassword"];
  const columns = provider === "microsoft" ? microsoft : google;
  const example = provider === "microsoft"
    ? {
        email: "casey.morgan@yourdomain.onmicrosoft.com",
        firstName: "Casey", lastName: "Morgan",
        appPassword: "xxxx-yyyy-zzzz",
        tenantId: "00000000-1111-2222-3333-444444444444",
        proxyProtocol: "socks5", proxyHost: "us-static.example.net",
        proxyPort: "10002", proxyUsername: "user-abc",
        proxyPassword: "proxy-secret-1",
      }
    : {
        email: "casey.morgan@yourcompany.com",
        firstName: "Casey", lastName: "Morgan",
        appPassword: "abcd efgh ijkl mnop",
        proxyProtocol: "socks5", proxyHost: "us-static.example.net",
        proxyPort: "10001", proxyUsername: "user-abc",
        proxyPassword: "proxy-secret-1",
      };
  const optionalNote = provider === "microsoft"
    ? "tenantId is optional (leave blank when not applicable). proxyProtocol accepts http, https, or socks5."
    : "proxyProtocol accepts http, https, or socks5.";

  return (
    <div className="border border-[#2A2A2A] bg-[#0A0A0A] mb-6" data-testid="csv-format-hint">
      <button
        onClick={() => setOpen(!open)}
        data-testid="csv-format-toggle"
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-[#141414] transition-colors"
      >
        <span className="flex items-center gap-2 text-xs text-[#E4E4E7] font-mono uppercase tracking-wider">
          <Info size={13} weight="fill" className="text-[#00E5FF]" />
          {providerLabel} CSV format — {columns.length} columns
        </span>
        <span className="text-[10px] text-[#71717A] font-mono">
          {open ? "hide" : "show"} details
        </span>
      </button>
      {open && (
        <div className="px-4 py-3 border-t border-[#2A2A2A] space-y-3">
          <div>
            <div className="micro mb-1.5 text-[#71717A]">Required column order</div>
            <div className="flex flex-wrap gap-1">
              {columns.map((c) => (
                <span key={c} className="font-mono text-[11px] px-2 py-0.5 bg-black border border-[#2A2A2A] text-[#E4E4E7]">
                  {c}
                </span>
              ))}
            </div>
          </div>
          <div>
            <div className="micro mb-1.5 text-[#71717A]">Example row</div>
            <div className="overflow-x-auto">
              <table className="text-[11px] font-mono border border-[#2A2A2A]">
                <thead className="bg-[#141414]">
                  <tr>
                    {columns.map((c) => (
                      <th key={c} className="px-2 py-1 text-left text-[#71717A] uppercase tracking-wider border-r border-[#2A2A2A] last:border-r-0">{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    {columns.map((c) => (
                      <td key={c} className="px-2 py-1 text-[#E4E4E7] border-r border-[#2A2A2A] last:border-r-0 whitespace-nowrap">
                        {example[c] || <span className="text-[#71717A]">(empty)</span>}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div className="text-[11px] text-[#71717A] leading-snug">
            {optionalNote} Header row is required. Duplicate emails are skipped.
            Passwords are encrypted at rest.
          </div>
        </div>
      )}
    </div>
  );
}


// ---- Validation modal ---------------------------------------------------
// Rendered after the user picks a CSV. Shows a diagnostic report (issues,
// warnings, duplicates, column mismatches) and lets them commit or cancel.
function ValidationModal({ report, fileName, onConfirm, onCancel }) {
  const importable = report.importable_rows || 0;
  const hasBlockers = (report.invalid_rows || []).length > 0
    || (report.missing_expected_columns || []).length > 0;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center overflow-y-auto p-6"
         data-testid="validation-modal">
      <div className="bg-[#0A0A0A] border border-[#2A2A2A] w-full max-w-4xl my-8">
        <div className="flex items-start justify-between px-5 py-4 border-b border-[#2A2A2A]">
          <div>
            <div className="micro mb-1 text-[#71717A]">CSV validation</div>
            <div className="text-lg font-heading font-bold tracking-tight text-white">
              {fileName || "senders.csv"}
            </div>
          </div>
          <button data-testid="validation-close" onClick={onCancel}
                  className="text-[#71717A] hover:text-white font-mono text-lg leading-none">×</button>
        </div>

        <div className="grid grid-cols-4 border-b border-[#2A2A2A]">
          <SummaryStat label="Total rows" v={report.total_rows} />
          <SummaryStat label="Importable" v={importable} tone={importable > 0 ? "success" : "muted"} />
          <SummaryStat label="Invalid" v={(report.invalid_rows || []).length} tone="error" />
          <SummaryStat label="Duplicates" v={(report.duplicates_in_file || []).length + (report.duplicates_in_db || []).length} tone="warn" />
        </div>

        {(report.missing_expected_columns || []).length > 0 && (
          <div className="px-5 py-3 border-b border-[#2A2A2A] bg-[#1a0a0a]">
            <div className="micro mb-1 text-[#FF3B30]">Missing required columns</div>
            <div className="flex flex-wrap gap-1">
              {report.missing_expected_columns.map((c) => (
                <span key={c} className="font-mono text-[11px] px-2 py-0.5 border border-[#FF3B30]/40 text-[#FF3B30]">{c}</span>
              ))}
            </div>
          </div>
        )}
        {(report.unknown_columns || []).length > 0 && (
          <div className="px-5 py-3 border-b border-[#2A2A2A] bg-[#1a1500]">
            <div className="micro mb-1 text-[#FFB800]">Unknown columns (ignored)</div>
            <div className="flex flex-wrap gap-1">
              {report.unknown_columns.map((c) => (
                <span key={c} className="font-mono text-[11px] px-2 py-0.5 border border-[#FFB800]/40 text-[#FFB800]">{c}</span>
              ))}
            </div>
          </div>
        )}

        <div className="px-5 py-4 space-y-5 max-h-[55vh] overflow-y-auto">
          <IssueTable
            title={`Blocking issues (${(report.invalid_rows || []).length})`}
            rows={report.invalid_rows}
            tone="error"
            testid="validation-invalid"
            emptyText="No blocking issues — all present rows will import."
          />
          <IssueTable
            title={`Warnings (${(report.warning_rows || []).length})`}
            rows={report.warning_rows}
            issueKey="warnings"
            tone="warn"
            testid="validation-warnings"
            emptyText="No warnings."
          />

          {(report.duplicates_in_file || []).length > 0 && (
            <div>
              <div className="micro mb-2 text-[#FFB800]">Duplicate emails within this file ({report.duplicates_in_file.length})</div>
              <div className="border border-[#2A2A2A] bg-black text-[11px] font-mono px-3 py-2 space-y-0.5" data-testid="validation-dup-file">
                {report.duplicates_in_file.map((d, i) => (
                  <div key={i} className="text-[#FFB800]">
                    row {d.row}: <span className="text-white">{d.email}</span>
                    <span className="text-[#71717A]"> (also on row {d.first_seen_row})</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(report.duplicates_in_db || []).length > 0 && (
            <div>
              <div className="micro mb-2 text-[#FFB800]">Already in database ({report.duplicates_in_db.length}) — will be skipped</div>
              <div className="border border-[#2A2A2A] bg-black text-[11px] font-mono px-3 py-2 space-y-0.5" data-testid="validation-dup-db">
                {report.duplicates_in_db.slice(0, 20).map((e) => (
                  <div key={e} className="text-[#FFB800]">{e}</div>
                ))}
                {report.duplicates_in_db.length > 20 && (
                  <div className="text-[#71717A]">…and {report.duplicates_in_db.length - 20} more</div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-5 py-4 border-t border-[#2A2A2A]">
          <div className="text-[11px] text-[#71717A] font-mono">
            {hasBlockers
              ? "Blocking rows will be skipped. Fix them in your CSV and re-upload for a full import."
              : "Everything checks out."}
          </div>
          <div className="flex gap-2">
            <BtnSecondary onClick={onCancel} testid="validation-cancel">Cancel</BtnSecondary>
            <BtnPrimary onClick={onConfirm} testid="validation-confirm"
                        disabled={importable === 0}>
              Import {importable} row{importable === 1 ? "" : "s"}
            </BtnPrimary>
          </div>
        </div>
      </div>
    </div>
  );
}

function SummaryStat({ label, v, tone }) {
  const color = tone === "success" ? "text-[#00FF66]"
              : tone === "error"   ? "text-[#FF3B30]"
              : tone === "warn"    ? "text-[#FFB800]"
              : "text-[#E4E4E7]";
  return (
    <div className="px-4 py-3 border-r border-[#2A2A2A] last:border-r-0">
      <div className="micro mb-1 text-[#71717A]">{label}</div>
      <div className={`data-num text-2xl font-bold ${color}`}>{v}</div>
    </div>
  );
}

function IssueTable({ title, rows, tone, testid, emptyText, issueKey = "issues" }) {
  const list = rows || [];
  const color = tone === "error" ? "text-[#FF3B30]" : "text-[#FFB800]";
  if (list.length === 0) {
    return (
      <div>
        <div className={`micro mb-2 ${color}`}>{title}</div>
        <div className="text-[11px] text-[#71717A] font-mono px-3 py-2 border border-dashed border-[#2A2A2A]">
          {emptyText}
        </div>
      </div>
    );
  }
  return (
    <div>
      <div className={`micro mb-2 ${color}`}>{title}</div>
      <div className="border border-[#2A2A2A]" data-testid={testid}>
        <table className="dtable w-full text-[11px]">
          <thead>
            <tr>
              <th style={{ width: 60 }}>Row</th>
              <th style={{ width: 220 }}>Email</th>
              <th>Issue{list.length === 1 ? "" : "s"}</th>
            </tr>
          </thead>
          <tbody>
            {list.slice(0, 100).map((r, i) => (
              <tr key={i}>
                <td className="font-mono text-[11px] text-[#71717A]">{r.row}</td>
                <td className="font-mono text-[11px]">{r.email || <span className="text-[#71717A]">(missing)</span>}</td>
                <td className={`font-mono text-[11px] ${color}`}>
                  {(r[issueKey] || []).join(" · ")}
                </td>
              </tr>
            ))}
            {list.length > 100 && (
              <tr><td colSpan={3} className="text-center text-[#71717A] font-mono py-2">
                …and {list.length - 100} more (fix these first, re-upload to review the rest)
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ---- Progress banner ----------------------------------------------------
// Reusable banner for long-running batched operations (bulk upload, bulk
// proxy test). Displays a title, live counts, a percentage bar, and a
// cancel button. Persists briefly (~4s) after completion so the user can
// read the final numbers.
export function ProgressBanner({ testid, title, progress, onCancel, stats }) {
  const total = Math.max(1, progress.total || 0);
  const done = progress.done || 0;
  const pct = Math.min(100, Math.round((done / total) * 100));
  const finished = done >= (progress.total || 0);
  const label = progress.cancelled ? "Cancelled" : finished ? "Complete" : "In progress";

  return (
    <div
      className="border border-[#2A2A2A] bg-[#0A0A0A] mb-6"
      data-testid={testid}
    >
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#2A2A2A]">
        <div className="flex items-center gap-3">
          <span className="micro text-[#E1FF00]">{title}</span>
          <span className="text-[11px] text-[#71717A] font-mono uppercase tracking-wider">
            · {label}
          </span>
        </div>
        {!finished && !progress.cancelled && (
          <button
            onClick={onCancel}
            data-testid={`${testid}-cancel`}
            className="text-[10px] text-[#71717A] hover:text-white font-mono uppercase tracking-wider inline-flex items-center gap-1"
          >
            <X size={11} weight="bold" /> Cancel
          </button>
        )}
      </div>

      <div className="px-4 py-3 space-y-2">
        <div className="flex items-center justify-between text-[11px] font-mono">
          <span className="text-[#A1A1AA]">
            <span className="text-white">{done}</span> / {progress.total} processed
          </span>
          <span className="text-[#E1FF00] data-num text-lg font-bold">{pct}%</span>
        </div>
        <div className="h-1.5 w-full bg-[#141414] overflow-hidden">
          <div
            className="h-full bg-[#E1FF00] transition-all duration-300"
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="flex gap-4 text-[11px] font-mono pt-1">
          {(stats || []).map((s) => {
            const color =
              s.tone === "success" ? "text-[#00FF66]" :
              s.tone === "error"   ? "text-[#FF3B30]" :
              s.tone === "warn"    ? "text-[#FFB800]" : "text-[#A1A1AA]";
            return (
              <span key={s.label} className={color}>
                {s.value} <span className="text-[#71717A]">{s.label.toLowerCase()}</span>
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}


// ---- Pagination -----------------------------------------------------------
// Reusable Prev / Next + "Showing X-Y of Z" + page-size selector. Renders
// nothing when total <= smallest page size so it stays out of the way on
// small tables. `onPageSizeChange` is optional — omit it to hide the size
// selector (some callers may want a fixed page size).
export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  sizeOptions = [25, 50, 100, 250, 500, 1000],
  testidPrefix = "pagination",
}) {
  if (!total || total <= Math.min(...sizeOptions)) {
    // Even when total <= smallest option we still let users switch page size
    // if they want, but hide when there's nothing to page through.
    if (!onPageSizeChange || total <= 25) return null;
  }
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = page * pageSize + 1;
  const end = Math.min((page + 1) * pageSize, total);
  const disabledPrev = page === 0;
  const disabledNext = (page + 1) * pageSize >= total;

  return (
    <div className="flex items-center justify-between mt-3 text-xs font-mono flex-wrap gap-2" data-testid={testidPrefix}>
      <div className="text-[#71717A]">
        Showing <span className="text-white">{Math.min(start, total)}</span>–
        <span className="text-white">{end}</span> of{" "}
        <span className="text-white">{total}</span>
      </div>
      <div className="flex items-center gap-3">
        {onPageSizeChange && (
          <label className="flex items-center gap-1.5 text-[#71717A]">
            <span className="uppercase tracking-wider text-[10px]">per page</span>
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              data-testid={`${testidPrefix}-size`}
              className="bg-black border border-[#2A2A2A] text-white px-2 py-1 text-xs font-mono focus:border-[#E1FF00] focus:outline-none"
            >
              {sizeOptions.map((s) => (<option key={s} value={s}>{s}</option>))}
            </select>
          </label>
        )}
        <button
          onClick={() => onPageChange(Math.max(0, page - 1))}
          disabled={disabledPrev}
          data-testid={`${testidPrefix}-prev`}
          className={`px-3 py-1 uppercase tracking-wider ${disabledPrev ? "text-[#3A3A3A] cursor-not-allowed" : "text-[#E4E4E7] hover:bg-[#141414] border border-[#2A2A2A]"}`}
        >
          ← Prev
        </button>
        <span className="text-[#71717A]">
          Page <span className="text-white">{page + 1}</span> / {totalPages}
        </span>
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={disabledNext}
          data-testid={`${testidPrefix}-next`}
          className={`px-3 py-1 uppercase tracking-wider ${disabledNext ? "text-[#3A3A3A] cursor-not-allowed" : "text-[#E4E4E7] hover:bg-[#141414] border border-[#2A2A2A]"}`}
        >
          Next →
        </button>
      </div>
    </div>
  );
}

